import { useCallback, useEffect, useState } from "react";

import { dayLabel, NotificationRow } from "@/components/NotificationBits";
import { useAuth } from "@/lib/auth-context";
import { listNotifications, markAllNotificationsRead } from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import type { Notification } from "@/lib/types";

type Filter = "unread" | "all";

export default function NotificationsPage() {
  useDocumentTitle("Notifications — SkillPatch");
  const { accessToken } = useAuth();
  const [filter, setFilter] = useState<Filter>("unread");
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const load = useCallback(() => {
    if (!accessToken) return;
    setLoading(true);
    listNotifications({ unreadOnly: filter === "unread", limit: 20 }, accessToken)
      .then((r) => {
        setItems(r);
        setHasMore(r.length === 20);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [filter, accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  async function loadMore() {
    if (!accessToken || items.length === 0) return;
    setLoadingMore(true);
    try {
      const more = await listNotifications(
        { unreadOnly: filter === "unread", limit: 20, before: items[items.length - 1].id },
        accessToken,
      );
      setItems((cur) => [...cur, ...more]);
      setHasMore(more.length === 20);
    } finally {
      setLoadingMore(false);
    }
  }

  async function markAllRead() {
    if (!accessToken) return;
    setItems((cur) => cur.map((n) => ({ ...n, read: true })));
    try {
      await markAllNotificationsRead(accessToken);
    } catch {
      /* optimistic */
    }
  }

  // Group consecutively by calendar day, in the order the (already newest-first) list arrives.
  const groups: { label: string; items: Notification[] }[] = [];
  for (const n of items) {
    const label = dayLabel(n.created_at);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(n);
    else groups.push({ label, items: [n] });
  }

  return (
    <main className="px-8 py-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-medium tracking-tight text-text">Notifications</h1>
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            {(["unread", "all"] as Filter[]).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`rounded-full px-4 py-2 font-display text-sm font-medium transition ${
                  filter === f ? "bg-white/[0.08] text-text" : "border border-white/[0.13] text-text/50 hover:text-text/80"
                }`}
              >
                {f === "unread" ? "Unread" : "All"}
              </button>
            ))}
          </div>
          <button onClick={markAllRead} className="font-mono text-xs uppercase tracking-wide text-text/40 transition hover:text-primary">
            Mark all read
          </button>
        </div>
      </div>

      <div className="mt-6 border-t border-white/10">
        {loading ? (
          <p className="py-10 text-center text-sm text-text/40">Loading…</p>
        ) : items.length === 0 ? (
          <p className="py-10 text-center text-sm text-text/40">
            {filter === "unread" ? "You're all caught up." : "No notifications yet."}
          </p>
        ) : (
          groups.map((g) => (
            <div key={g.label}>
              <div className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-text/45">
                <span className="h-px w-5 bg-primary" />
                {g.label}
              </div>
              <div>
                {g.items.map((n, i) => (
                  <div key={n.id} className={i > 0 ? "border-t border-[var(--border)]" : ""}>
                    <NotificationRow n={n} token={accessToken}
                      onResolved={() => setItems((cur) => cur.map((x) => (x.id === n.id ? { ...x, read: true } : x)))} />
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {!loading && items.length > 0 && hasMore && (
        <button
          onClick={loadMore}
          disabled={loadingMore}
          className="mt-6 w-full rounded-lg border border-white/15 py-2.5 font-display text-sm font-medium text-text/70 transition hover:border-white/30 disabled:opacity-50"
        >
          {loadingMore ? "Loading…" : "Load more"}
        </button>
      )}
    </main>
  );
}
